import { FC, useRef, useState } from 'react'
import { Button, Divider, Form, Modal, notification, Space, Tabs, Typography } from 'antd'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import Calculation from '../../../../types/api/calculation'
import CalculationPosition from '../../../../types/api/calculation-position'
import { calculationPositionsQuery } from '../../constants/calculation-positions-query'
import { useForm } from 'antd/es/form/Form'
import TabsPanel from './components/tabs-panel'
import FormValues, { ListItem } from './types/form-values'
import isValidationErrorEntity from '../../../../helpers/type-guards/is-validate-error-entity'
import { ValidateErrorEntity } from '../../../../types/forms/antd'
import { useCreateShipmentPositionMutation } from '../../../../store/api/shipment-positions-api'
import {
    useCreateShipmentMutation,
    useSetShipmentToolingInclusionMutation,
    useUpdateAndCalculateShipmentMutation,
} from '../../../../store/api/shipments-api'
import { useChooseOpenOrder } from '../../../../helpers/orders/use-choose-open-order'
import { useUpdateCalculationPositionMutation } from '../../../../store/api/calculation-positions-api'
import Shipment from '../../../../types/api/shipment'
import useInitialValues from './hooks/use-initial-values'
import useCreateShipmentQueryArgExtractor from './hooks/use-create-shipment-query-arg-extractor'
import useCreateShipmentPositionQueryArgExtractor from './hooks/use-create-shipment-position-query-arg-extractor'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../../../constants/routes'
import useUploadFiles from '../../../../hooks/api/use-upload-file'
import { useCreateTaskMutation } from '../../../../store/api/tasks-api'
import extractCreateDrawingBlankTaskArg from '../../../../helpers/tasks/extract-create-drawing-blank-task'
import UploadedFile from '../../../../types/api/uploaded-file'
import DevFillActions from './components/dev-fill-actions'

interface ShipmentCreateOutcome {
    item: ListItem
    shipment?: Shipment
    toolingError?: unknown
    createError?: unknown
}

interface ShipmentCreationFormProps {
    calculation: Calculation
    onSuccess: () => void
    onCalculationPositionUpdated?: (position: CalculationPosition) => void
}

const ShipmentCreationForm: FC<ShipmentCreationFormProps> = ({
    calculation: calc,
    onSuccess,
    onCalculationPositionUpdated,
}) => {
    const navigate = useNavigate()
    const [form] = useForm<FormValues>()
    const createdPositionIdsRef = useRef(new Set<string>())
    // @ts-ignore
    window.formdt = form
    const [createTask, createTaskState] = useCreateTaskMutation()
    const [tabsWithErrors, setTabsWithErrors] = useState<Set<number>>(new Set())
    const [createShipmentPosition, createShipmentPositionState] =
        useCreateShipmentPositionMutation()
    const [createShipment, createShipmentState] = useCreateShipmentMutation()
    const [updateAndCalculateShipment, updateAndCalculateShipmentState] =
        useUpdateAndCalculateShipmentMutation()
    const [setShipmentToolingInclusion, setShipmentToolingInclusionState] =
        useSetShipmentToolingInclusionMutation()
    const chooseOpenOrder = useChooseOpenOrder()
    const [updateCalculationPosition, updateCalculationPositionState] =
        useUpdateCalculationPositionMutation()
    const [uploadFiles] = useUploadFiles()

    const loading =
        createShipmentState.isLoading ||
        updateAndCalculateShipmentState.isLoading ||
        setShipmentToolingInclusionState.isLoading ||
        createShipmentPositionState.isLoading ||
        createTaskState.isLoading ||
        updateCalculationPositionState.isLoading

    const initialValues = useInitialValues(calc)
    const extractCreateShipmentPositionQueryArg = useCreateShipmentPositionQueryArgExtractor()
    const extractCreateShipmentQueryArg = useCreateShipmentQueryArgExtractor(calc)

    const defineAndSetTabsWithErrors = (e: ValidateErrorEntity) => {
        const set = new Set<number>()
        e.errorFields.forEach(item => {
            set.add(item.name[1])
        })
        setTabsWithErrors(set)
    }

    const handleAndCreateShipmentPosition = async (
        pos: ListItem['positions'][number],
        item: ListItem
    ) => {
        let filesParam: { sourceFile: UploadedFile } | undefined = undefined
        console.log('handleAndCreateShipmentPosition', pos)

        if (pos.sourceFile && pos.sourceFile.length > 0) {
            const uploadedFile = await uploadFiles(pos.sourceFile)
            if (uploadedFile.data && uploadedFile.data[0]) {
                filesParam = { sourceFile: uploadedFile.data[0] }
            }
        }

        const queryArg = extractCreateShipmentPositionQueryArg(pos, item, filesParam)
        const res = await createShipmentPosition(queryArg)
        if (res.data) return res.data
        return null
    }
    const handleAndCreateAllShipmentPositions = async (item: ListItem) => {
        const docIds: string[] = []

        for (let i = 0; i < item.positions.length; i++) {
            const res = await handleAndCreateShipmentPosition(item.positions[i], item)
            if (res) docIds.push(res.data.documentId)
        }

        return docIds
    }

    const handleAndCreateAllShipmentSourceFiles = async (item: ListItem) => {
        const docIds: number[] = []
        console.log('handleAndCreateAllShipmentSourceFiles', item)
        if (item.sourceFile && item.sourceFile.length) {
            const uploadedFile = await uploadFiles(item.sourceFile)
            if (uploadedFile.data) {
                for (const file of uploadedFile.data) {
                    docIds.push(file.id)
                }
            }
        }
        return docIds
    }

    const applyToolingFlags = async (shipmentDocId: string, position: CalculationPosition) => {
        const stampExcluded =
            position.stampIncludedInCost === false &&
            (position.calculated?.stampCost ?? 0) > 0 &&
            Boolean(position.stamp?.documentId)
        const formsExcluded =
            position.formsIncludedInCost === false && (position.calculated?.formsCost ?? 0) > 0

        if (stampExcluded) {
            await setShipmentToolingInclusion({
                docId: shipmentDocId,
                kind: 'stamp',
                included: false,
            }).unwrap()
        }

        if (formsExcluded) {
            await setShipmentToolingInclusion({
                docId: shipmentDocId,
                kind: 'forms',
                included: false,
            }).unwrap()
        }

        if (!stampExcluded && !formsExcluded) {
            await updateAndCalculateShipment({
                docId: shipmentDocId,
                body: { data: {} },
            }).unwrap()
        }
    }

    const handleAndCreateShipment = async (
        item: ListItem,
        orderDocumentId?: string
    ): Promise<ShipmentCreateOutcome> => {
        try {
            const shipmentPositionDocIds = await handleAndCreateAllShipmentPositions(item)
            const sourceFileIds = await handleAndCreateAllShipmentSourceFiles(item)
            const queryArg = await extractCreateShipmentQueryArg(
                item,
                shipmentPositionDocIds,
                sourceFileIds,
                orderDocumentId
            )
            const createdShipment = await createShipment(queryArg).unwrap()

            try {
                await applyToolingFlags(createdShipment.data.documentId, item.calculationPosition)
            } catch (toolingError) {
                return { item, shipment: createdShipment.data, toolingError }
            }

            return { item, shipment: createdShipment.data }
        } catch (createError) {
            return { item, createError }
        }
    }

    const resolveExistingOrderId = async (): Promise<string | undefined> => {
        const clientDocumentId = calc.client?.documentId
        if (!clientDocumentId) {
            return undefined
        }

        const choice = await chooseOpenOrder(clientDocumentId, 'отгрузку')
        return choice.kind === 'existing' ? choice.documentId : undefined
    }

    const createAllShipments = async (orderDocumentId?: string) => {
        const list = form.getFieldsValue().list.filter(item => {
            const docId = item.calculationPosition?.documentId
            return !docId || !createdPositionIdsRef.current.has(docId)
        })

        return Promise.all(list.map(item => handleAndCreateShipment(item, orderDocumentId)))
    }

    const markCalculationPositionsAsCreated = async (list: ListItem[]) => {
        for (const item of list) {
            const calcPosDocId = item.calculationPosition?.documentId
            if (!calcPosDocId) continue

            const res = await updateCalculationPosition({
                docId: calcPosDocId,
                body: {
                    data: {
                        createdShipmentPosition: true,
                    },
                },
                queryParams: calculationPositionsQuery,
            }).unwrap()
            onCalculationPositionUpdated?.(res.data)
        }
    }

    const createTaskOfBlankCreation = async (shipment: Shipment) => {
        const args = extractCreateDrawingBlankTaskArg(shipment)
        const res = await createTask(args)

        if (res.error) {
            Modal.error({ title: 'Ошибка создания задачи', content: JSON.stringify(res.error) })
        }
    }

    const isPreviousBlankPreviewReused = (item: ListItem) => {
        const previousBlankPreviewFiles =
            item.calculationPosition.shipment_position?.shipment?.blankPreviewFiles

        return Boolean(item.reusePreviousBlankPreview && previousBlankPreviewFiles?.length)
    }

    const createTasksOfBlankCreationForAllShipments = async (
        shipments: Shipment[],
        list: ListItem[]
    ) => {
        for (let i = 0; i < shipments.length; i++) {
            const shipment = shipments[i]
            const item = list[i]
            if (item && isPreviousBlankPreviewReused(item)) continue

            await createTaskOfBlankCreation(shipment)
        }
    }

    const onSend = async () => {
        try {
            await form.validateFields()
            const orderDocumentId = await resolveExistingOrderId()
            const outcomes = await createAllShipments(orderDocumentId)
            const created = outcomes.filter(outcome => outcome.shipment)

            for (const outcome of created) {
                const docId = outcome.item.calculationPosition?.documentId
                if (docId) createdPositionIdsRef.current.add(docId)
            }

            const toolingMessages = created
                .filter(outcome => outcome.toolingError)
                .map(outcome => readApiErrorMessage(outcome.toolingError))

            if (toolingMessages.length) {
                Modal.error({
                    title: 'Не удалось изменить включение оснастки',
                    content: (
                        <div>
                            {toolingMessages.map((message, index) => (
                                <p key={index}>{message}</p>
                            ))}
                        </div>
                    ),
                })
            }

            if (created.length) {
                await markCalculationPositionsAsCreated(created.map(outcome => outcome.item))
                await createTasksOfBlankCreationForAllShipments(
                    created.map(outcome => outcome.shipment as Shipment),
                    created.map(outcome => outcome.item)
                )
            }

            if (outcomes.some(outcome => outcome.createError)) {
                notification.error({ message: 'Проверьте указанные данные' })
                return
            }

            onSuccess()

            if (!toolingMessages.length) {
                notification.success({
                    message: (
                        <div>
                            <p>Сборки перенесены в отгрузки</p>
                            <Typography.Link
                                onClick={() => {
                                    navigate(routes.shipments)
                                }}
                            >
                                Посмотреть
                            </Typography.Link>
                        </div>
                    ),
                })
            }
        } catch (e) {
            console.error(e)
            notification.error({ message: 'Проверьте указанные данные' })
            if (isValidationErrorEntity(e)) defineAndSetTabsWithErrors(e)
        }
    }

    const onValuesChange = async () => {
        try {
            await form.validateFields({ validateOnly: true })
        } catch (e) {
            console.error(e)
            if (isValidationErrorEntity(e)) defineAndSetTabsWithErrors(e)
        }
    }

    return (
        <>
            <Title style={{ marginTop: 0 }} level={2}>
                Форма создания отгрузок
            </Title>
            <Text type={'success'}>Пожалуйста, заполните и проверьте данные</Text>

            <br />
            <Space size={24}>
                <p>
                    Клиент: <b>{calc.client?.name}</b>
                </p>
            </Space>

            <Divider orientation='left'>
                <Title style={{ margin: 0 }} level={3}>
                    Сборки
                </Title>
            </Divider>

            <Form
                form={form}
                layout={'vertical'}
                initialValues={initialValues}
                onValuesChange={onValuesChange}
            >
                <Form.List name={'list'}>
                    {(fields, { add, remove }) => {
                        const isTabWithError = (i: number) => tabsWithErrors.has(i)

                        return (
                            <Tabs
                                tabPosition={'left'}
                                items={initialValues.list?.map((pos, i) => ({
                                    label: (
                                        <Typography.Text
                                            type={isTabWithError(i) ? 'danger' : undefined}
                                        >
                                            № {i + 1}
                                        </Typography.Text>
                                    ),
                                    key: String(pos?.calculationPosition?.documentId),
                                    children: <TabsPanel name={i} />,
                                    forceRender: true,
                                }))}
                            />
                        )
                    }}
                </Form.List>
            </Form>
            <p></p>
            <Space>
                <DevFillActions form={form} />
                <Button type={'primary'} loading={loading} onClick={onSend}>
                    Отправить
                </Button>
            </Space>
        </>
    )
}

function readApiErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
        const err = error as {
            data?: { error?: { message?: string }; message?: string }
            message?: string
        }
        const message = err.data?.error?.message || err.data?.message || err.message
        if (message) return message
    }

    return 'Не удалось изменить включение оснастки'
}

export default ShipmentCreationForm
